/* eslint-disable import/no-extraneous-dependencies */
import { Marker, Popup, useMap } from 'react-leaflet';
import L, { LeafletMouseEvent } from 'leaflet';
import React, { useMemo, useEffect } from 'react';
import 'leaflet.markercluster/dist/MarkerCluster.css';
import 'leaflet.markercluster/dist/MarkerCluster.Default.css';
// eslint-disable-next-line @typescript-eslint/no-var-requires
import MarkerClusterGroup from '@changey/react-leaflet-markercluster';
import { useStore } from '../../../../store';
import { useMapContext } from '../context/map-context';
import { createClusterCustomIcon, createPinIcon } from './map-icons/icons';
import { PinPopup } from '../components/PinPopup';

export const CustomMarkers = ():JSX.Element => {
  const { mapOrigin, mapReport } = useStore();
  const {
    clusterGroupRef,
    onClusterClick,
    imageAngle,
    rangePerDegree,
    pinDigits,
    onSelectPin,
    selectedPin,
  } = useMapContext();
  const map = useMap();

  const handleClusterClick = (event: LeafletMouseEvent) => {
    const cluster = event.propagatedFrom;
    if (cluster instanceof L.MarkerCluster) {
      const markersInCluster = cluster.getAllChildMarkers();
      const optionsArray = markersInCluster.map((marker) => marker.options.options.marker);
      onClusterClick(optionsArray);
    }
  };

  const rangeToCoordinates = (rangeX: number, rangeY: number): [number, number] | null => {
    if (!mapOrigin.data.latitude || !mapOrigin.data.longitude || !rangePerDegree) return null;

    const latitude = Number(mapOrigin.data.latitude) + rangeY / rangePerDegree.lat;
    const longitude = Number(mapOrigin.data.longitude) + rangeX / rangePerDegree.lon;

    return [latitude, longitude];
  };

  function rotatePoint(
    point: [number, number],
    center: [number, number],
    angle: number,
  ): [number, number] {
    const angleRad = (angle * Math.PI) / 180;

    const pointProjected = map.project(L.latLng(point));
    const centerProjected = map.project(L.latLng(center));

    // Calculate deltas
    const deltaX = pointProjected.x - centerProjected.x;
    const deltaY = pointProjected.y - centerProjected.y;

    // Apply rotation
    const rotatedX = deltaX * Math.cos(angleRad) - deltaY * Math.sin(angleRad);
    const rotatedY = deltaX * Math.sin(angleRad) + deltaY * Math.cos(angleRad);

    // Return the rotated point relative to the center
    const rotatedPointProjected = L.point(
      centerProjected.x + rotatedX,
      centerProjected.y + rotatedY,
    );
    const points = map.unproject(rotatedPointProjected);
    return [points.lat, points.lng];
  }

  const memoizedMarkers = useMemo(() => {
    const markers = Object.values(mapReport.data.recordsMap).map((marker) => {
      const rawX = Number(marker?.X || marker?.x);
      const rawY = Number(marker?.Y || marker?.y);
      if (!Number.isFinite(rawX) || !Number.isFinite(rawY)) {
        return null;
      }

      const position = rangeToCoordinates(
        Number(rawX), Number(rawY),
      );
      const rotatePosition = position
        && rotatePoint(
          [position[0], position[1]],
          [Number(mapOrigin.data.latitude), Number(mapOrigin.data.longitude)],
          imageAngle,
        );

      if (!rotatePosition) return null;

      return (
        <Marker
          key={marker.uniqueKey}
          position={[rotatePosition[0], rotatePosition[1]]}
          icon={createPinIcon(marker, pinDigits)}
          options={{ marker }}
          eventHandlers={{
            click: () => onSelectPin(marker),
          }}
        >
          <Popup closeButton={false}>
            <PinPopup marker={marker} />
          </Popup>
        </Marker>
      );
    });

    return (
      <MarkerClusterGroup
        spiderfyDistanceMultiplier={2}
        maxClusterRadius={50}
        ref={clusterGroupRef}
        showCoverageOnHover={false}
        zoomToBoundsOnClick
        iconCreateFunction={createClusterCustomIcon}
        eventHandlers={{
          clusterclick: handleClusterClick, // Attach the cluster click event handler
        }}
      >
        {markers}
      </MarkerClusterGroup>
    );

    return <>{markers}</>;
  }, [
    mapReport.data.recordsMap,
    imageAngle, pinDigits,
  ]);

  useEffect(() => {
    if (!selectedPin) {
      map.closePopup();
    }
  }, [selectedPin]);

  return (
    <>
      {memoizedMarkers}
    </>
  );
};
